// pages/support/DashboardPage.tsx
import { useEffect, useState } from 'react';
import { Phone, Ticket, Users, TrendingUp, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import PageListLayout from '../layouts/PageListLayout';
import useCalls from '../hooks/support/calls/useCalls';
import useTickets from '../hooks/support/tickets/useTickets';
import useClients from '../hooks/support/clients/useClients';
import { Call } from '../types/features/support/Call';
import { Ticket as TicketType } from '../types/features/support/Ticket';
import CallDetailsModal from '../components/features/support/calls/CallDetailsModal';
import TicketDetailsModal from '../components/features/support/tickets/TicketDetailsModal';
import { alertError, alertSuccess } from '../helpers/alertError';

export default function DashboardPage() {
  const { calls, fetchCalls, loading: callsLoading } = useCalls();
  const { tickets, fetchTickets, updateTicket, loading: ticketsLoading, pagination } = useTickets();
  const { clients, fetchClients, loading: clientsLoading } = useClients();
  
  const [callToView, setCallToView] = useState<Call | null>(null);
  const [ticketToView, setTicketToView] = useState<TicketType | null>(null);
  const [recentCalls, setRecentCalls] = useState<Call[]>([]);
  const [recentTickets, setRecentTickets] = useState<TicketType[]>([]);
  const [stats, setStats] = useState({
    callsToday: 0,
    openTickets: 0,
    totalClients: 0,
    resolvedToday: 0
  });

  useEffect(() => {
    fetchCalls(1, 50);
    fetchTickets(1, 50, { open_only: true });
    fetchClients();
  }, []);

  useEffect(() => {
    if (calls.length > 0) {
      const today = new Date().toISOString().split('T')[0];
      const todayCalls = calls.filter(c => c.created_at?.startsWith(today));

      setStats(prev => ({
        ...prev,
        callsToday: todayCalls.length,
        totalClients: clients.length
      }));

      setRecentCalls(calls.slice(0, 5));
    }
  }, [calls, clients]);

  useEffect(() => {
    if (tickets.length > 0) {
      const openTickets = tickets.filter(t => t.status !== 'resolu' && t.status !== 'ferme');
      const resolvedToday = tickets.filter(t => {
        if (!t.resolved_at) return false;
        const today = new Date().toISOString().split('T')[0];
        return t.resolved_at.startsWith(today);
      });

      setStats(prev => ({
        ...prev,
        openTickets: openTickets.length,
        resolvedToday: resolvedToday.length
      }));

      setRecentTickets(tickets.slice(0, 5));
    }
  }, [tickets]);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getClientNameFromCall = (call: Call) => {
    if (call.client?.firstname && call.client?.lastname) {
      return `${call.client.firstname} ${call.client.lastname}`;
    }
    if (call.client?.phone) {
      return call.client.phone;
    }
    return call.client_phone || 'Inconnu';
  };

  const getTicketPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critique': return 'bg-red-100 text-red-800';
      case 'haute': return 'bg-orange-100 text-orange-800';
      case 'moyenne': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-green-100 text-green-800';
    }
  };

  const handleTicketStatusChange = async (id: string, status: TicketType['status']) => {
    const success = await updateTicket(id, { status });
    if (success) {
      alertSuccess(`Ticket ${status === 'resolu' ? 'résolu' : status === 'en_cours' ? 'pris en charge' : 'mis à jour'}`);
      setTicketToView(null);
      fetchTickets(1, 50, { open_only: true });
    } else {
      alertError('Erreur lors de la mise à jour');
    }
  };

  const loading = callsLoading || ticketsLoading || clientsLoading;

  return (
    <>
      <PageListLayout title="Tableau de bord">
        <div className="p-6">
          {/* En-tête */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900">
              Bonjour, Service Client
            </h1>
            <p className="text-gray-500 mt-1">
              {new Date().toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          </div>

          {/* Statistiques */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Appels aujourd'hui</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.callsToday}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Phone className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Tickets en cours</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.openTickets}</p>
                </div>
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                  <Ticket className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Clients</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.totalClients}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Résolus aujourd'hui</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.resolvedToday}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Derniers appels et tickets */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Derniers appels */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="flex justify-between items-center p-5 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Derniers appels</h2>
                <Link to="/support/calls" className="text-sm text-green-600 hover:text-green-700 flex items-center gap-1">
                  Voir tous <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="divide-y">
                {loading ? (
                  <div className="p-8 text-center text-gray-500">Chargement...</div>
                ) : recentCalls.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">Aucun appel enregistré</div>
                ) : (
                  recentCalls.map((call) => (
                    <div 
                      key={call.id} 
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setCallToView(call)}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-gray-900">
                            {getClientNameFromCall(call)}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded ${call.direction === 'entrant' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
                              {call.direction === 'entrant' ? 'Entrant' : 'Sortant'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDate(call.created_at)}
                            </span>
                          </div>
                        </div>
                        <button className="text-sm text-gray-400 hover:text-gray-600">
                          Voir
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Derniers tickets */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="flex justify-between items-center p-5 border-b">
                <h2 className="text-lg font-semibold text-gray-900">Tickets en cours</h2>
                <Link to="/support/tickets" className="text-sm text-green-600 hover:text-green-700 flex items-center gap-1">
                  Voir tous <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="divide-y">
                {loading ? (
                  <div className="p-8 text-center text-gray-500">Chargement...</div>
                ) : recentTickets.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">Aucun ticket en cours</div>
                ) : (
                  recentTickets.map((ticket) => (
                    <div 
                      key={ticket.id} 
                      className="p-4 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setTicketToView(ticket)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {ticket.title}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded ${getTicketPriorityColor(ticket.priority)}`}>
                              {ticket.priority}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDate(ticket.created_at)}
                            </span>
                          </div>
                        </div>
                        <button className="text-sm text-gray-400 hover:text-gray-600 ml-4">
                          Voir
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </PageListLayout>

      {/* Modal détails appel */}
      {callToView && (
        <CallDetailsModal
          isOpen={!!callToView}
          onClose={() => setCallToView(null)}
          call={callToView}
        />
      )}

      {/* Modal détails ticket */}
      {ticketToView && (
        <TicketDetailsModal
          isOpen={!!ticketToView}
          onClose={() => setTicketToView(null)}
          ticket={ticketToView}
          onStatusChange={handleTicketStatusChange}
        />
      )}
    </>
  );
}